import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatConfidence, num } from "@/lib/matching";

// One scored counterpart against a threshold, shared by the suggestion list and candidate search so both
// draw "close to the line" the same way.
export function SuggestionRow({
  kind,
  href,
  number,
  description,
  detail,
  confidence,
  boosted,
  threshold,
  thresholdLabel = "Auto-accept threshold",
  meta,
  dimmed = false,
}: {
  kind: "PO" | "DN";
  href: string;
  number: string;
  description: string;
  detail: string;
  confidence: number | string;
  boosted: boolean;
  threshold: number | string;
  // Which threshold the marker is: auto-accept for a composed-score suggestion, similarity for a name-only search result.
  thresholdLabel?: string;
  // Extra line under the bar (date, status, remaining). Optional: suggestions already sit inside a bill line.
  meta?: ReactNode;
  // A candidate the engine couldn't offer (consumed or closed): still shown, never mistakable for a live suggestion.
  dimmed?: boolean;
}) {
  const pct = Math.max(0, Math.min(1, num(confidence)));
  const clears = pct >= num(threshold);

  return (
    <div className={`rounded-md border px-3 py-2 ${dimmed ? "opacity-60" : ""}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-muted-foreground w-6 shrink-0 text-[10px] font-semibold uppercase">
          {kind}
        </span>
        <Link to={href} className="font-mono text-sm hover:underline">
          {number}
        </Link>
        <span className="text-muted-foreground truncate text-sm">{description}</span>
        <span className="text-muted-foreground ml-auto text-xs tabular-nums">{detail}</span>
        <span
          className={`text-xs font-semibold tabular-nums ${clears ? "text-foreground" : "text-muted-foreground"}`}
        >
          {formatConfidence(pct)}
        </span>
      </div>

      {/* The threshold is a marker so the gap is visible, not arithmetic. */}
      <div className="bg-muted relative mt-1.5 h-1.5 w-full rounded-full">
        <div
          className={`h-1.5 rounded-full ${clears ? "bg-primary" : "bg-muted-foreground/50"}`}
          style={{ width: `${pct * 100}%` }}
        />
        <div
          className="bg-foreground/70 absolute top-[-2px] h-[10px] w-px"
          style={{ left: `${Math.min(1, Math.max(0, num(threshold))) * 100}%` }}
          title={`${thresholdLabel} ${formatConfidence(threshold)}`}
        />
      </div>

      {meta && <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">{meta}</div>}

      {boosted && (
        <div className="text-muted-foreground mt-1 text-[11px]">
          Ranked higher because someone linked another line of this bill to the same document. The uplift
          moves this suggestion up the list only — auto-accept judges the raw score, so a manual link can
          neither create an automatic match nor suppress one.
        </div>
      )}
    </div>
  );
}
